import { c as _c } from "react/compiler-runtime";
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { type ReactNode, createContext, useCallback, useContext, useMemo, useReducer } from 'react';

/* eslint-disable @typescript-eslint/no-unsafe-return */
export function memoize<T extends (...args: any[]) => any>(fn: T, cmpFn: (a: Parameters<T>, b: Parameters<T>) => boolean = (a, b) => a === b) {
  let lastArgs: Parameters<T> | null = null;
  let lastResult: ReturnType<T> | null = null;
  return (...args: Parameters<T>): ReturnType<T> => {
    if (lastArgs && cmpFn(lastArgs, args)) {
      return lastResult as ReturnType<T>;
    }
    lastArgs = args;
    lastResult = fn(...args);
    return lastResult as ReturnType<T>;
  };
}
function useStore(getInitialState, reducer) {
  const $ = _c(3);
  const [state, dispatch_] = useReducer(reducer, undefined, getInitialState);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = action => {
      dispatch_(action);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dispatch = t0;
  let t1;
  if ($[1] !== state) {
    t1 = [state, dispatch];
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1 as const;
}
const StoreCtx = createContext<ReturnType<typeof useStore<any, any>> | null>(null);
const InnerProvider = StoreCtx.Provider;
export function StoreProvider(t0) {
  const $ = _c(6);
  const {
    children,
    getInitialState,
    reducer
  } = t0;
  const [state, dispatch] = useStore(getInitialState, reducer);
  let t1;
  if ($[0] !== dispatch || $[1] !== state) {
    t1 = [state, dispatch];
    $[0] = dispatch;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <InnerProvider value={t1}>{children}</InnerProvider>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function useStoreContext() {
  const store = useContext(StoreCtx);
  if (!store) {
    throw new Error("Store not found");
  }
  return store as ReturnType<typeof useStore<T, A>>;
}
export function useSelector(selector) {
  const $ = _c(5);
  let t0;
  if ($[0] !== selector) {
    t0 = memoize(selector);
    $[0] = selector;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const memoisedSelector = t0;
  const [state] = useStoreContext();
  let t1;
  if ($[2] !== memoisedSelector || $[3] !== state) {
    t1 = memoisedSelector(state);
    $[2] = memoisedSelector;
    $[3] = state;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export function useDispatch() {
  const [, dispatch] = useStoreContext();
  return dispatch;
}
export type CreateStoreToolsReturn<T extends Record<string, unknown>, A extends {
  type: string;
}> = {
  Provider: React.ComponentType<{
    children: ReactNode;
    getInitialState: () => T;
  }>;
  useSelector: <S extends (state: T) => any>(selector: S) => ReturnType<S>;
  useDispatch: () => (action: A) => void;
};
export function createStoreTools<T extends Record<string, unknown>, A extends {
  type: string;
}>(reducer: (state: T, action: A) => T): CreateStoreToolsReturn<T, A> {
  return {
    Provider: function Provider({
      children,
      getInitialState
    }: {
      children: React.ReactNode;
      getInitialState: () => T;
    }) {
      return <StoreProvider getInitialState={getInitialState} reducer={reducer}>
          {children}
        </StoreProvider>;
    },
    useSelector: useSelector<T>,
    useDispatch: useDispatch<A>
  };
}